import { Bot } from 'lucide-react'

import { Avatar } from '~/components/Avatar'
import { OrgMember, UserKind } from '~/types'

const DEFAULT_PROFILE_PHOTO =
  'https://assets.splits.org/default_profile_photo.png'

// A 24px avatar gets a 14px badge, the smallest size where its glyph stays legible.
const AGENT_BADGE_RATIO = 14 / 24
// Keeps the glyph clear of the badge's edge at every size.
const AGENT_BADGE_GLYPH_INSET = 4
// Below this the badge is under 12px and its glyph under 8px, which reads as a dot. The 16px timeline avatars skip it.
const AGENT_BADGE_MIN_AVATAR_SIZE = 20

export const UserAvatar = ({
  user,
  size = 20,
}: {
  size?: number
  user: Pick<OrgMember, 'email' | 'displayName' | 'profilePhotoUrl' | 'kind'>
}) => {
  const { email, displayName, profilePhotoUrl, kind } = user
  const badgeSize = Math.round(size * AGENT_BADGE_RATIO)
  const showsAgentBadge =
    kind === UserKind.Agent && size >= AGENT_BADGE_MIN_AVATAR_SIZE
  // Sized like the Avatar so a grid or flex parent cannot stretch the wrapper and drag the badge away from the corner.
  const dimension = `${size / 16}rem`

  return (
    <span
      style={{ width: dimension, height: dimension }}
      className="relative inline-block shrink-0"
    >
      <Avatar
        image={profilePhotoUrl ?? undefined}
        // Default photo only backs a broken URL; no photo falls to the initial
        fallbackImage={profilePhotoUrl ? DEFAULT_PROFILE_PHOTO : undefined}
        initial={(displayName ?? email ?? '').at(0)?.toUpperCase()}
        alt={`Avatar for ${displayName ?? email ?? ''}`}
        size={size}
      />
      {showsAgentBadge && (
        <span
          role="img"
          aria-label="Agent"
          style={{ width: badgeSize, height: badgeSize }}
          className="absolute -bottom-0.5 -right-0.5 grid place-items-center rounded-full bg-gray-100 text-gray-700 ring-1 ring-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:ring-gray-600"
        >
          <Bot size={badgeSize - AGENT_BADGE_GLYPH_INSET} />
        </span>
      )}
    </span>
  )
}
